import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { lookInsidePages } from '../data/lookInsidePages';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const getStep = () => {
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.offsetWidth + gap;
  };
  const updateState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = getStep();
    const maxScroll = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft >= maxScroll - 4);
    if (step > 0) {
      const idx = track.scrollLeft >= maxScroll - 4 ? lookInsidePages.length - 1 : Math.round(track.scrollLeft / step);
      setActive(Math.min(idx, lookInsidePages.length - 1));
    }
  }, []);
  useEffect(() => {
    updateState();
    window.addEventListener('resize', updateState);
    return () => window.removeEventListener('resize', updateState);
  }, [updateState]);
  const scrollByCards = (dir: 1 | -1) => {
    trackRef.current?.scrollBy({
      left: dir * getStep(),
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (i: number) => {
    trackRef.current?.scrollTo({
      left: i * getStep(),
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="look-inside-heading" className="w-full bg-cream text-ink" data-id="element-131" data-mp="3seudw15zbmrm">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-132" data-mp="2zrzvrawmwzhb">
        <div className="text-center" data-id="element-133" data-mp="2zrgnklwsdpgh">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-134" data-mp="13ov3xu5m4h6r">
            Look inside
          </p>
          <h2 id="look-inside-heading" className="mt-3 font-display font-semibold text-plum text-3xl sm:text-5xl" data-id="element-135" data-mp="18n528dmmvxwl">
            Open the week. Pick 30, 45 or 55. Teach.
          </h2>
          <p className="mx-auto mt-4 max-w-[60ch] text-base text-ink" data-id="element-136" data-mp="1heylw8xgloqe">
            Real pages from the pack: every block written in 8-counts with BPM,
            cues, and easier and harder options.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-137" data-mp="pr6jgnfgjc28">
          <ul ref={trackRef} onScroll={updateState} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:gap-6 md:scroll-px-0 md:px-0" data-id="element-138" data-mp="tq5w6u7fkd48">
            {lookInsidePages.map((page, i) => <li key={page.src} className="w-[80%] shrink-0 snap-start md:w-[calc((100%-3rem)/3)]" data-id="element-139" data-mp="2a3meyqog9iei" data-mpi={i}>
                <figure data-id="element-140" data-mp="2f9rjt5zweiyi">
                  <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full size: ${page.caption}`} className="block w-full overflow-hidden rounded-2xl border border-blush bg-white shadow-card transition-transform duration-200 ease-out hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50" data-id="element-141" data-mp="2f9hqb8fn9to2">
                    <img src={page.src} alt={page.caption} loading="lazy" width={900} height={1600} className="aspect-[9/16] w-full object-contain" data-id="element-142" data-mp="1wlcs0ghrb0wn" />
                  </button>
                  <figcaption className="mt-3 text-center text-sm text-ink-soft" data-id="element-143" data-mp="3hww3gjlx0p85">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollByCards(-1)} disabled={atStart} aria-label="Previous pages" className="absolute -left-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-left-6" data-id="element-144" data-mp="3tz5ratwyvdp0">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-145" data-mp="2w92fwrv6nrm7" />
          </button>
          <button type="button" onClick={() => scrollByCards(1)} disabled={atEnd} aria-label="Next pages" className="absolute -right-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-right-6" data-id="element-146" data-mp="31ahy8c5z7k0w">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-147" data-mp="2dn3cnyc9mutz" />
          </button>
        </div>

        <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Choose page" data-id="element-148" data-mp="2rm2xeuwnqpkv">
          {lookInsidePages.map((page, i) => <button key={page.src} type="button" role="tab" aria-selected={active === i} aria-label={`Go to page ${i + 1}: ${page.caption}`} onClick={() => scrollToIndex(i)} className="flex h-6 w-6 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full" data-id="element-149" data-mp="2mn01bzynntp1" data-mpi={i}>
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-plum' : 'w-2 bg-blush-dark'}`} data-id="element-150" data-mp="35by39k07gvud" />
            </button>)}
        </div>

        <div className="mt-10 flex flex-col items-center" data-id="element-151" data-mp="2bzzz2yziehji">
          <p className="text-sm text-ink-soft" data-id="element-152" data-mp="2gs9kc1poitfg">
            6 volumes · 52 weeks · one-time $27
          </p>
          <div className="mt-4 w-full max-w-md" data-id="element-153" data-mp="2lq1d0xy5ejkp">
            <CtaButton href="#pricing" data-id="element-154" data-mp="2ltgrpznwtqll">Get all 52 weeks</CtaButton>
          </div>
        </div>
      </div>

      <PageLightbox pages={lookInsidePages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-155" data-mp="2vpae0arfkygk" />
    </section>;
}